import Component from "../../lib/component";
import { JsonFetcher } from "../../lib/json_fetcher";
import { POST_HTTP_METHOD } from "../../helpers/request";
import { MixinPersistentStore } from "../../lib/mixins/persistable";
import FormSerialize from "form-serialize";
import Submission from "./models/submission";
import createMemberOfferAcceptorStateMachine from "./instances/member_offer_acceptor_state_machine";
import { AuthClient, AuthStatus } from "../../lib/auth/auth_client";
import { resolveMessage, PASSWORD_PANEL_OVERRIDES } from "../../lib/auth/auth_messages";
import { AcceptForm } from "./components/accept_form";
import { AccountForm } from "./components/account_form";
import { VerifyForm } from "./components/verify_form";
import { PasswordForm } from "./components/password_form";
import { ForgotForm } from "./components/forgot_form";
import { observeSessionState } from "../../lib/session_state";
import { getThrottledFunction } from "../../helpers/throttle";

export const MemberOfferAcceptorConfig = {
  componentTypeKey: "MemberOfferAcceptor",
  baseHtmlClass: "member_offer_acceptor",
  selector: ".member_offer_acceptor",

  // Dataset config
  datasetConfigKey: "member_offer_acceptor__config",
  datasetConfigEndpointKey: "endpoint",
  datasetConfigLocalStorageKey: "local_storage_key",

  // Self-owned inline auth endpoints
  endpointRequestOtpKey: "request_otp_endpoint",
  endpointVerifyOtpKey: "verify_otp_endpoint",
  endpointVerifyPasswordKey: "verify_password_endpoint",
  endpointRequestPasswordResetKey: "request_password_reset_endpoint",
  endpointSessionStateKey: "session_state_endpoint",
  endpointLogoutKey: "logout_endpoint",

  // Local Storage
  defaultLocalStorageKey: "MEMBER_OFFER_ACCEPTOR",
  storedSubmissionsKey: "submissions",
  storedOwnerPersonIdKey: "ownerPersonId",

  // Stage
  stageSelector: ".member_offer_acceptor__stage",
  stageHeightProperty: "--moa-stage-height",

  // Form
  formObjectName: "member_offer_acceptance",
  // The accept endpoint is POST (routes.rb: :member => { :accept => :post }). Held
  // here rather than read off the form element, which declares no method.
  acceptHttpMethod: POST_HTTP_METHOD,
};

export const EndpointStatuses = {
  idle: "idle",
  loading: "loading",
  error: "error",
  success: "success",
  failure: "failure",
};

// The accept flow's copy, in one place — the counterpart to lib/auth/auth_messages.js for
// the auth flow. (The server supplies the success/validation copy; these are the messages
// the CLIENT has to write itself, because the server never answered.)
export const AcceptMessages = {
  connecting: "Connecting to Rankers.co.nz…",
  networkError: "An error occurred. Perhaps you have no network access. Please try again.",
  timeout: "The server did not respond in time. Do you have internet? Please try again.",
  // A response arrived, but not one we can read. Nothing to do with the visitor's
  // connection — saying so would send them off chasing their wifi.
  badResponse: "Something went wrong at our end. Please try again.",
  alreadySent: (email) => `We've already sent an email to ${email}. Please check your inbox.`,
};

// Thrown when the accept endpoint answers with something unreadable. Distinct from a
// network failure so the visitor is told the truth about which one happened.
export class MalformedResponseError extends Error {}

// MemberOfferAcceptor — the Camping NZ ferry-discount signup.
//
// The host is a slim orchestrator: it owns the state machine (ALL card state — phase,
// auth, and the offer-accept status/message), the AuthClient calls, the localStorage
// submissions store (+ identity-scoping), the session-state detector, and the
// offer-accept fetch. It sets the phase and fans render() out to the five look-alike
// panels; each panel then decides for ITSELF whether it's the one showing (--active /
// --inactive on its shared .member_offer_acceptor__form block).
//
// ONE state discipline: every piece of card state is machine state, mutated by dispatch,
// and a dispatch repaints. There are no hand-placed render() calls outside the
// constructor's first paint.
export class MemberOfferAcceptor extends Component {
  static get config() {
    return MemberOfferAcceptorConfig;
  }

  constructor(id_or_element) {
    super({
      element: id_or_element,
      config: MemberOfferAcceptor.config,
      stateMachine: createMemberOfferAcceptorStateMachine(),
    });

    this.setDatasetConfig(MemberOfferAcceptor.config.datasetConfigKey);
    this.endpoint = this.getDatasetConfigValue(MemberOfferAcceptor.config.datasetConfigEndpointKey);
    this.stageElement = this.element.querySelector(MemberOfferAcceptor.config.stageSelector);

    const localStorageKey = this.getDatasetConfigValue(
      MemberOfferAcceptor.config.datasetConfigLocalStorageKey,
    ) || MemberOfferAcceptor.config.defaultLocalStorageKey;

    // Persistent local storage for the submissions (and responses).
    MixinPersistentStore(this);
    this.setPersistentStore(window.localStorage);
    this.setPersistentStorageKey(localStorageKey);
    this.submissions = this.getStoredSubmissions();

    // Self-owned inline auth (plans/2026-07-11…). The AuthClient drives the server
    // flow; this host owns the five panels + phase logic.
    this.authClient = new AuthClient({
      requestOtpUrl: this.getDatasetConfigValue(MemberOfferAcceptor.config.endpointRequestOtpKey),
      verifyOtpUrl: this.getDatasetConfigValue(MemberOfferAcceptor.config.endpointVerifyOtpKey),
      verifyPasswordUrl: this.getDatasetConfigValue(MemberOfferAcceptor.config.endpointVerifyPasswordKey),
      requestPasswordResetUrl: this.getDatasetConfigValue(MemberOfferAcceptor.config.endpointRequestPasswordResetKey),
      logoutUrl: this.getDatasetConfigValue(MemberOfferAcceptor.config.endpointLogoutKey),
    });
    this.sessionStateEndpoint = this.getDatasetConfigValue(MemberOfferAcceptor.config.endpointSessionStateKey);

    // The five form panels — siblings, not a hierarchy (parent-manager pattern: the
    // host constructs them and fans render() out; each shares this host's state
    // machine and owns its own elements, listeners, visibility and render). All five
    // are server-rendered unconditionally, so a missing panel is a markup bug: let it
    // throw here rather than silently leaving an inert card.
    //
    // Adopted, so destroying this host destroys the panels with it. They are constructed
    // here rather than by the loader, so they are not in the ComponentRegistry and this
    // host is the only thing that can ever tear them down.
    this.panels = this.adoptChildComponents(
      this.acceptForm = new AcceptForm(this.element.querySelector(AcceptForm.config.selector), this),
      this.accountForm = new AccountForm(this.element.querySelector(AccountForm.config.selector), this),
      this.verifyForm = new VerifyForm(this.element.querySelector(VerifyForm.config.selector), this),
      this.passwordForm = new PasswordForm(this.element.querySelector(PasswordForm.config.selector), this),
      this.forgotForm = new ForgotForm(this.element.querySelector(ForgotForm.config.selector), this),
    );

    // Prefill the accept fields from the last submission (identity-scoping below
    // clears it on a person switch).
    const lastSubmission = this.getLastSubmission();
    if (lastSubmission) this.acceptForm.updateFormFields(lastSubmission);

    // The accept outcome STICKS across page loads: a stored submission the server
    // accepted means this browser has claimed its card, so the card must come back in
    // its success state (green + tick + locked fields) rather than resetting to a
    // fresh-looking form. The localStorage store is the persistence — endpointStatus
    // is just re-derived from it here. (Only success persists: a loading/error/failure
    // outcome is about one in-flight attempt, and re-offering the form is right.)
    //
    // Seeded AFTER the panels exist: these are dispatches now, and a dispatch repaints
    // the card — which fans out to the panels.
    const wasAccepted = !!(lastSubmission && lastSubmission.isAccepted());
    if (wasAccepted) this.setAcceptMessage(AcceptMessages.alreadySent(lastSubmission.email));
    this.setEndpointStatus(wasAccepted ? EndpointStatuses.success : EndpointStatuses.idle);

    this.render();

    // The stage height is measured (renderStageHeight), so a viewport change must
    // re-measure it. Window listeners outlive the element — hence onDestroy.
    const handleResize = getThrottledFunction(this.renderStageHeight.bind(this), 100);
    window.addEventListener("resize", handleResize);
    this.onDestroy(() => window.removeEventListener("resize", handleResize));

    // Session-state observer so the card knows a session exists and can render the
    // identity row + "Sign out". Keeps the detector to re-check it after an accept
    // auto-login / an explicit sign-out.
    //
    // onEveryCheck: the card doesn't just RENDER the session, it re-validates its
    // persisted store against it (reconcileSubmissionsOwner). That has to run on every
    // confirmed check, not only on transitions — a load after a sign-out that happened
    // on another page is logged-out → logged-out, no transition, no event, yet the
    // stale claimed store is exactly what must be cleared.
    //
    // The detector is a page-wide singleton, so it outlives this card: the pane is
    // re-injected on every open, and an observer left attached would keep every dead
    // card alive — each one still reconciling the same localStorage store the live card
    // owns. Hence onDestroy: the reaper detaches us when the pane goes.
    if (this.sessionStateEndpoint) {
      const session = observeSessionState(this.handleSessionState.bind(this), {
        endpointUrl: this.sessionStateEndpoint,
        onEveryCheck: true,
      });
      this.detector = session.detector;
      this.onDestroy(session.stop);
    }
  }

  // --- Offer accept ----------------------------------------------------------

  // Every path through the accept flow ends in a dispatch, and a dispatch repaints the
  // card — so there are no hand-placed render() calls here (and no `.finally(render)`).
  submitAccept() {
    const jsonFetcher = new JsonFetcher(this.endpoint, {
      httpMethod: MemberOfferAcceptor.config.acceptHttpMethod,
      timeoutCallback: this.handleEndpointLateData.bind(this),
      timeout: 10000,
    });
    let params = this.getFormParams();

    this.setAcceptMessage(AcceptMessages.connecting);
    this.setEndpointStatus(EndpointStatuses.loading);

    const submission = Submission.newFromParams(params[MemberOfferAcceptor.config.formObjectName]);
    this.storeSubmission(submission);
    params[MemberOfferAcceptor.config.formObjectName]["guid"] = submission.guid;

    jsonFetcher
      .fetchData(params)
      .then(this.handleEndpointData.bind(this))
      .catch(this.handleEndpointDataError.bind(this));
  }

  getFormParams() {
    return FormSerialize(this.acceptForm.element, { hash: true, empty: false });
  }

  // The catch-all for the accept fetch — which catches BOTH a request that never landed
  // and a response we could not read (handleEndpointData's throw comes through here too).
  // They are not the same failure and must not carry the same message: blaming the
  // visitor's connection for our own bad payload sends them off checking their wifi.
  handleEndpointDataError(error) {
    console.error("[MemberOfferAcceptor]", error);
    const isOurFault = error instanceof MalformedResponseError;
    this.setAcceptMessage(isOurFault ? AcceptMessages.badResponse : AcceptMessages.networkError);
    this.setEndpointStatus(EndpointStatuses.error);
  }

  handleEndpointLateData() {
    this.setAcceptMessage(AcceptMessages.timeout);
    this.setEndpointStatus(EndpointStatuses.error);
  }

  handleEndpointData(data) {
    if (typeof data !== "object" || data === null) {
      throw new MalformedResponseError("[MemberOfferAcceptor] handleEndpointData() `data` must be an object");
    }

    this.setAcceptMessage(data.message);
    const moaData = data[MemberOfferAcceptor.config.formObjectName];

    if (data.status === "success" && moaData) {
      const submission = this.submissions.find((s) => s.guid === moaData.guid);
      if (submission) {
        submission.updateFromServer(moaData);
        this.acceptForm.updateFormFields(submission);
      }
      this.setStoreItem(MemberOfferAcceptor.config.storedSubmissionsKey, this.submissions);

      this.setEndpointStatus(EndpointStatuses.success);

      // Accepting a NEW email auto-creates a Person + session (plans/2026-07-08…).
      // Announcing is all this takes: it re-reads the session, which paints the identity
      // row here (handleSessionState) and fans the change out to the other native webviews.
      window.Rankers_NS?.announceSessionChange?.();
    } else {
      this.setEndpointStatus(EndpointStatuses.failure);
    }
  }

  // --- Auth intents (phase transitions) --------------------------------------

  // Open the sign-in flow → email-entry (account) panel. Carry the email the visitor
  // already entered / had remembered on the accept form into the sign-in flow (via
  // authEmail, which the account panel prefills from) so they don't retype it.
  openSignIn() {
    const acceptEmail = this.acceptForm.getEmail();
    if (acceptEmail) this.dispatchAction("setAuthEmail", acceptEmail);
    this.dispatchAction("setPhase", "account");
    this.dispatchAction("setAuthStatus", null);
  }

  // Abandon sign-in → back to the accept panel (the escape hatch from email entry).
  closeSignIn() {
    this.dispatchAction("setPhase", "accept");
    this.dispatchAction("setAuthStatus", null);
  }

  // Re-enter a different email → back to the account panel (from verify/password).
  changeEmail() {
    this.dispatchAction("setPhase", "account");
    this.dispatchAction("setAuthStatus", null);
  }

  // Open the in-card forgot-password panel (from the password panel).
  openForgot() {
    this.dispatchAction("setPhase", "forgot");
    this.dispatchAction("setAuthStatus", null);
  }

  // Return from the forgot panel to the password panel it came from.
  closeForgot() {
    this.dispatchAction("setPhase", "password");
    this.dispatchAction("setAuthStatus", null);
  }

  async beginSignIn(rawEmail) {
    const email = (rawEmail || "").trim();
    if (!email) {
      this.dispatchAction("setAuthStatus", { code: AuthStatus.INVALID_EMAIL, tone: "error", text: "Please enter your email address." });
      return;
    }
    this.dispatchAction("setAuthEmail", email);
    this.startBusy("SENDING");
    const { statusCode } = await this.authClient.requestCode(email);
    this.endBusy();
    this.applyRequestResult(statusCode);
  }

  async resendCode() {
    if (!this.canResend()) return;
    this.startBusy("SENDING");
    const { statusCode } = await this.authClient.requestCode(this.authEmail());
    this.endBusy();
    this.applyRequestResult(statusCode);
  }

  async submitCode(rawCode) {
    const code = (rawCode || "").trim();
    if (!code) {
      this.dispatchAction("setAuthStatus", { code: AuthStatus.INVALID_CODE, tone: "error", text: "Please enter the code from your email." });
      return;
    }
    this.startBusy("CHECKING");
    const { statusCode } = await this.authClient.verifyCode(this.authEmail(), code);
    this.endBusy();
    if (statusCode === AuthStatus.AUTHENTICATED) return this.handleAuthenticated();
    this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
  }

  async submitPassword(rawPassword) {
    const password = rawPassword || "";
    if (!password) {
      this.dispatchAction("setAuthStatus", { code: AuthStatus.INVALID_CREDENTIALS, tone: "error", text: "Please enter your password." });
      return;
    }
    this.startBusy("SIGNING_IN");
    const { statusCode } = await this.authClient.verifyPassword(this.authEmail(), password);
    this.endBusy();
    if (statusCode === AuthStatus.AUTHENTICATED) return this.handleAuthenticated();
    this.dispatchAction("setAuthStatus", resolveMessage(statusCode, PASSWORD_PANEL_OVERRIDES, this.statusCtx()));
  }

  // In-card forgot-password request. Enumeration-uniform — always RESET_SENT (or a
  // NETWORK_ERROR); Back returns to the password panel.
  async requestPasswordReset(rawEmail) {
    const email = (rawEmail || "").trim();
    if (!email) {
      this.dispatchAction("setAuthStatus", { code: AuthStatus.INVALID_EMAIL, tone: "error", text: "Please enter your email address." });
      return;
    }
    this.dispatchAction("setAuthEmail", email);
    this.startBusy("SENDING_RESET");
    const { statusCode } = await this.authClient.requestPasswordReset(email);
    this.endBusy();
    this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
  }

  async logoutSession() {
    this.startBusy("SIGNING_OUT");
    await this.authClient.logout();
    this.endBusy();
    // Explicit logout clears the identity-scoped store + prefill (shared-device).
    //
    // resetAuth FIRST, then clear: AcceptForm refills the email/name fields from the
    // signed-in identity on every render, so blanking them while `loggedIn` is still true
    // would just paint them straight back in.
    this.dispatchAction("resetAuth");
    this.clearSubmissionsStore();
    this.setStoreItem(MemberOfferAcceptor.config.storedOwnerPersonIdKey, null);
    if (this.detector) this.detector.check();
  }

  // Reset the card to a fresh claim. The escape hatch from a completed but signed-OUT
  // accept: an email that already belongs to a person accepts fine yet mints no
  // session, so there's no "Sign out" to clear the card and the success state would
  // otherwise stick forever (locked fields, no way for a second person on the same
  // device to apply). Signed-in success doesn't need this — "Sign out" already resets.
  resetAcceptance() {
    this.clearSubmissionsStore(); // blanks the fields + identifiers, back to idle
  }

  // Nothing to do locally. The AuthClient announced the moment it authenticated, and the
  // announce re-reads the session — which reaches this card through handleSessionState and
  // paints the identity row. This branch exists to SUPPRESS the status line: success here is
  // the identity row appearing, not a message about it.
  handleAuthenticated() {}

  applyRequestResult(statusCode) {
    switch (statusCode) {
      case AuthStatus.CODE_SENT:
      case AuthStatus.THROTTLED:
        this.dispatchAction("setPhase", "verify");
        this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
        break;
      case AuthStatus.HAS_USER_ACCOUNT:
        this.dispatchAction("setPhase", "password");
        this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
        break;
      default: // INVALID_EMAIL | NETWORK_ERROR | UNKNOWN_ERROR
        this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
    }
  }

  // --- Session state + identity-scoping --------------------------------------

  // Called for EVERY confirmed session check (see onEveryCheck above), logged in or out —
  // NOT only on transitions. The store reconciliation therefore runs unconditionally: on a
  // fresh page load the state machine's `loggedIn` starts false, so anything gated on it
  // can't see a sign-out that happened on some other page, and the card would come back in
  // its stale success state.
  //
  // Both dispatches are guarded against disturbing a visitor mid-flow, because a check can
  // land at any time (an accept auto-login, announceSessionChange, the native re-check poke)
  // and both actions force the phase back to accept:
  //
  //   • logged out → resetAuth is gated HERE on `loggedIn`, so a routine logged-out check
  //     doesn't yank someone out of the verify/password panel they're signing in through.
  //   • logged in  → signedIn is gated INSIDE THE ACTION, which distinguishes a transition
  //     (fresh sign-in / person switch — reset the phase) from a re-confirmation of the
  //     same person (refresh the identity only). See the action's comment.
  //
  // The gates are the same idea on the two sides; the signed-in one lives in the action so
  // it ports with the pattern (the Rankometer will have signed-in-reachable panels).
  handleSessionState(state) {
    // Belt and braces. destroy() detaches us from the detector, so a discarded card should
    // never be called again — but the whole point of the detach is that a dead card must not
    // write to the localStorage store the LIVE card owns, and that must not hinge on nobody
    // else holding a reference to this handler.
    if (this.isDestroyed) return;

    const personId = (state && state.personId) || null;

    // Sign out BEFORE reconciling the store — same reason as logoutSession(): clearing the
    // store repaints the card, and AcceptForm refills the email/name fields from the
    // signed-in identity, so a repaint while `loggedIn` is still true would paint the
    // departed person's details straight back into the fields we just blanked.
    if (!personId && this.readStateMachineValue("loggedIn")) this.dispatchAction("resetAuth");

    this.reconcileSubmissionsOwner(personId);

    if (personId) {
      // `name` (null for a not-yet-named account) lets AcceptForm unlock the name field
      // for a nameless account so the typed name is captured; displayName keeps its email
      // fallback for the identity row and named-account prefill.
      this.dispatchAction("signedIn", { id: personId, displayName: state.displayName, email: state.email, name: state.name });
    }

    // No render() call: anything above that CHANGES the card dispatches, and a dispatch
    // repaints. A check that changes nothing has nothing to repaint.
  }

  // The store is either UNCLAIMED or CLAIMED, and `ownerPersonId` says which.
  //
  // CLAIMED (owner set) — it belongs to that person and survives only while that person
  // IS the current session. A different person, or no session at all, means the claim is
  // stale: clear it. (This is what catches a sign-out performed anywhere else on the
  // site — it needs no logout event, only the session state.)
  //
  // UNCLAIMED (owner null) — an anonymous submitter's own acceptance. It is KEPT with no
  // session: accepting an email that ALREADY belongs to a Person succeeds but mints no
  // session, so there is no "Sign out" to clear it and Reset is its escape hatch. A
  // null→person sign-in keeps it too, and claims it for that person.
  //
  // Clearing waits for `hasChecked` — a server-confirmed state. `lastKnown` is seeded from
  // localStorage and a stale "logged out" seed would otherwise destroy a live person's
  // store a moment before the check restored their session.
  reconcileSubmissionsOwner(personId) {
    const owner = this.getStoreItem(MemberOfferAcceptor.config.storedOwnerPersonIdKey);

    if (owner && owner !== personId && this.detector && this.detector.hasChecked) {
      this.clearSubmissionsStore();
      this.setStoreItem(MemberOfferAcceptor.config.storedOwnerPersonIdKey, null);
      return;
    }

    if (personId) this.setStoreItem(MemberOfferAcceptor.config.storedOwnerPersonIdKey, personId);
  }

  clearSubmissionsStore() {
    this.submissions = [];
    this.setStoreItem(MemberOfferAcceptor.config.storedSubmissionsKey, []);
    this.acceptForm.blankFormFields();
    this.setAcceptMessage(null);
    this.setEndpointStatus(EndpointStatuses.idle);
  }

  // --- Rendering -------------------------------------------------------------

  render() {
    // Card-wide state only. The PHASE is NOT a host class: each panel owns its own
    // visibility (--active / --inactive on .member_offer_acceptor__form), so the host
    // just fans render() out and every panel decides whether it's the one showing.
    // Likewise the endpoint state: the Go button paints its own --loading / --success
    // (AcceptSubmitButton); the host paints success alone, because success recolours
    // the WHOLE card and reveals the Reset escape hatch.
    this.conditionallySetHtmlClassState(this.submissionWasSuccessful(), "success");
    this.conditionallySetHtmlClassState(!!this.readStateMachineValue("loggedIn"), "logged_in");
    this.conditionallySetHtmlClassState(!!this.readStateMachineValue("authBusy"), "auth_busy");

    // Fan out; each panel decides whether it's the one showing.
    this.panels.forEach((panel) => panel.render());

    this.renderStageHeight();
  }

  // The stage's height tracks the ACTIVE panel so phase changes animate the card's
  // size (CSS can't transition height: auto). Measured here and handed to the CSS
  // via a custom prop (_stage.scss) rather than styling inline. A zero measurement
  // (hidden pane, jsdom) leaves the pre-JS auto fallback in place.
  renderStageHeight() {
    const phase = this.readStateMachineValue("phase");
    const activePanel = this.panels.find((panel) => panel.config.phase === phase);
    if (!activePanel) return;

    const height = activePanel.element.offsetHeight;
    if (!height) return;

    this.stageElement.style.setProperty(MemberOfferAcceptor.config.stageHeightProperty, `${height}px`);
  }

  // --- Auth helpers ----------------------------------------------------------

  authEmail() { return this.readStateMachineValue("authEmail"); }
  statusCtx() { return { email: this.authEmail() }; }

  // The resend cooldown. The AuthClient owns the clock; panels ask the host, never the
  // host's collaborators — one coupling rule, and the panels stay ignorant of the
  // AuthClient entirely.
  canResend() { return this.authClient.canResend(); }
  resendSecondsRemaining() { return this.authClient.resendSecondsRemaining(); }

  startBusy(statusKey) {
    this.dispatchAction("setAuthBusy", true);
    this.dispatchAction("setAuthStatus", resolveMessage(statusKey, {}, this.statusCtx()));
  }
  endBusy() { this.dispatchAction("setAuthBusy", false); }

  // --- Offer-accept state ----------------------------------------------------

  getEndpointStatus() { return this.readStateMachineValue("endpointStatus") || EndpointStatuses.idle; }
  setEndpointStatus(status) { this.dispatchAction("setEndpointStatus", status); }
  setAcceptMessage(text) { this.dispatchAction("setAcceptMessage", text); }
  submissionWasSuccessful() { return this.getEndpointStatus() === EndpointStatuses.success; }

  // A rejected submission — the server refusing it (failure: validation errors, which
  // it returns as the message) or the request never landing (error: network/timeout).
  // Both put the accept hint into its error tone.
  submissionWasRejected() {
    return (
      this.getEndpointStatus() === EndpointStatuses.failure ||
      this.getEndpointStatus() === EndpointStatuses.error
    );
  }

  // The store holds AT MOST ONE submission — the latest attempt. Nothing reads the history:
  // the prefill and the success re-derivation both want the last one, the guid lookup after
  // a response targets the in-flight one (always the latest), and identity-scoping clears
  // the lot. Appending every attempt just grew localStorage forever.
  //
  // A late response for a SUPERSEDED attempt now finds no guid match and is skipped, which
  // is the behaviour that was already wanted.
  storeSubmission(submission) {
    this.submissions = [submission];
    this.setStoreItem(MemberOfferAcceptor.config.storedSubmissionsKey, this.submissions);
  }

  // Stored as an array, and read as one, for compatibility with the multi-entry stores
  // already sitting in visitors' browsers — of which only the last entry ever mattered.
  getStoredSubmissions() {
    const submissionsProps = this.getStoreItem(MemberOfferAcceptor.config.storedSubmissionsKey) || [];
    const latestProps = submissionsProps[submissionsProps.length - 1];
    return latestProps ? [new Submission(latestProps)] : [];
  }

  getLastSubmission() {
    return this.submissions[this.submissions.length - 1];
  }
}
